import {
  CONTENT_FREE_REJECTION_MESSAGE_PATTERN,
  DATADOG_SDK_ERROR_SOURCE,
  DEFAULT_ERROR_TYPE,
  FIRST_PARTY_HOST_PATTERN,
  FIRST_PARTY_CHUNK_URL_PATTERN,
  MAX_NORMALIZED_MESSAGE_LENGTH,
  NO_FIRST_PARTY_FRAME_FINGERPRINT_PREFIX,
} from "@/lib/errors/no-first-party-frame-fingerprint/constants";

/**
 * True when at least one `at ` frame in the stack points at a
 * `/_next/static/` chunk served from our origin. Frameless stacks,
 * `[native code]`-only stacks, extension URLs and frames that point at the
 * page document itself (inline or injected scripts) are all "no first-party
 * frame".
 */
export function hasFirstPartyFrame(
  stack: string | undefined,
  currentOrigin: string | undefined = _getCurrentOrigin(),
): boolean {
  if (!stack) {
    return false;
  }

  return stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "))
    .some((frame) => _isFirstPartyFrame(frame, currentOrigin));
}

/**
 * True when the stack has at least one `at ` frame of any origin. The RUM SDK
 * writes every browser's stack in that shape, and when it has none it puts
 * the placeholder `No stack, consider using an instance of Error` there
 * instead (a thrown or rejected non-Error, such as `{}`, an empty reason or a
 * DOM `Event`).
 */
export function hasStackFrame(stack: string | undefined): boolean {
  if (!stack) {
    return false;
  }

  return stack.split("\n").some((line) => line.trim().startsWith("at "));
}

/**
 * Collapses the parts of an error message that vary between occurrences of
 * the same bug: UUIDs, quoted ids, hex, numbers and the minified receiver
 * binding (`s.`, `n.`, `e.`), which changes on every build. Property names
 * survive, because minifiers don't rename them and they are what tells two
 * errors apart.
 */
export function normalizeErrorMessage(message: string): string {
  const normalized = message
    .replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
      "<uuid>",
    )
    .replace(/(['"`])([\w-]{8,})\1/g, (match, quote: string, body: string) =>
      /\d/.test(body) ? `${quote}<id>${quote}` : match,
    )
    .replace(/\b0x[0-9a-f]+\b/gi, "<hex>")
    .replace(/\b(?=[0-9a-f]*\d)(?=[0-9a-f]*[a-f])[0-9a-f]{8,}\b/gi, "<hex>")
    .replace(/(^|[^\w$])\d+(?:\.\d+)?/g, "$1<n>")
    .replace(/(^|[\s'"`(\[])[A-Za-z_$][\w$]?\.(?=[A-Za-z_$])/g, "$1<r>.")
    .replace(/\s+/g, " ")
    .trim();

  return normalized.slice(0, MAX_NORMALIZED_MESSAGE_LENGTH);
}

/**
 * Builds an Error Tracking fingerprint (type + normalized message) for an
 * error whose stack has no first-party frame. Datadog's default grouping
 * keys on stack frames, so with none of ours to key on it lumps unrelated
 * injected-script errors into one issue under whichever title it saw first.
 *
 * Returns `undefined` when the stack does have a first-party frame (default
 * grouping is right for those), when there is no message to key on, or when
 * the message is a content-free rejection (`Uncaught "Empty reason"`,
 * `Uncaught {}`) that carries nothing to key on either.
 */
export function buildNoFirstPartyFrameFingerprint({
  type,
  message,
  stack,
  currentOrigin,
}: {
  type: string | undefined;
  message: string | undefined;
  stack: string | undefined;
  currentOrigin?: string;
}): string | undefined {
  if (!message?.trim()) {
    return undefined;
  }

  if (
    !type?.trim() &&
    CONTENT_FREE_REJECTION_MESSAGE_PATTERN.test(message.trim())
  ) {
    return undefined;
  }

  if (hasFirstPartyFrame(stack, currentOrigin)) {
    return undefined;
  }

  const errorType = type?.trim() || DEFAULT_ERROR_TYPE;
  return `${NO_FIRST_PARTY_FRAME_FINGERPRINT_PREFIX}:${errorType}:${normalizeErrorMessage(message)}`;
}

/**
 * The Logs-chain variant. Only a log that already carries `error.kind` gets a
 * fingerprint. Kind-less error logs (Logs SDK network failures such as
 * `XHR error GET ...` with a status 0, or a `console.error` of a plain
 * string) never formed an Error Tracking issue on their own, and stamping
 * `error.fingerprint` on them promotes each one into a new, untitled issue
 * that pages. The goal is to regroup errors Error Tracking already tracks,
 * not to start tracking new ones.
 */
export function buildLogNoFirstPartyFrameFingerprint({
  kind,
  message,
  stack,
  currentOrigin,
}: {
  kind: string | undefined;
  message: string | undefined;
  stack: string | undefined;
  currentOrigin?: string;
}): string | undefined {
  if (!kind?.trim()) {
    return undefined;
  }

  return buildNoFirstPartyFrameFingerprint({
    type: kind,
    message,
    stack,
    currentOrigin,
  });
}

/**
 * The RUM-chain variant. An error with `source: "agent"` is a notice the
 * Datadog SDK raises about itself, such as `Reached max number of errors by
 * minute: 3000` when a page floods its rate limiter. These have no stack and
 * no type, never formed an Error Tracking issue on their own, and the error
 * that caused the flood is already tracked under its own issue. Stamping a
 * fingerprint on them opens a new, untitled issue that pages.
 *
 * An error with no stack frame at all (a thrown or rejected non-Error, such as
 * a `<link>` load-failure `Event` reported as `Uncaught {"type":"error",...}`)
 * gets no fingerprint either. Default grouping keys on frames, so these never
 * formed an issue, and a fingerprint would open a new, untitled one per
 * distinct message. Same goal as the Logs variant: regroup errors Error
 * Tracking already tracks, not start tracking new ones.
 *
 * The exception is a typed error whose stack is only its own `Type: message`
 * header, such as an extension's `TypeError` in Safari. Those do form an issue
 * under default grouping, all in one bucket under a stale title, so they get
 * a fingerprint like the Logs copy of the same error does.
 */
export function buildRumNoFirstPartyFrameFingerprint({
  source,
  type,
  message,
  stack,
  currentOrigin,
}: {
  source: string | undefined;
  type: string | undefined;
  message: string | undefined;
  stack: string | undefined;
  currentOrigin?: string;
}): string | undefined {
  if (
    source === DATADOG_SDK_ERROR_SOURCE ||
    (!hasStackFrame(stack) && !_isTypedMessageOnlyStack(type, stack))
  ) {
    return undefined;
  }

  return buildNoFirstPartyFrameFingerprint({
    type,
    message,
    stack,
    currentOrigin,
  });
}

/**
 * True when a frameless stack starts with the error's own type, as in
 * `TypeError: undefined is not an object`. The SDK's
 * `No stack, consider using an instance of Error` placeholder and a missing
 * type both read as false.
 */
function _isTypedMessageOnlyStack(
  type: string | undefined,
  stack: string | undefined,
): boolean {
  const errorType = type?.trim();
  if (!errorType || !stack) {
    return false;
  }

  return stack.trim().startsWith(`${errorType}:`);
}

function _isFirstPartyFrame(
  frame: string,
  currentOrigin: string | undefined,
): boolean {
  const match = FIRST_PARTY_CHUNK_URL_PATTERN.exec(frame);
  if (!match) {
    return false;
  }

  const frameOrigin = match[1];
  if (!frameOrigin) {
    // A relative chunk path resolves against the page, so it is ours.
    return true;
  }

  if (currentOrigin && frameOrigin === currentOrigin) {
    return true;
  }

  try {
    return FIRST_PARTY_HOST_PATTERN.test(new URL(frameOrigin).hostname);
  } catch {
    return false;
  }
}

function _getCurrentOrigin(): string | undefined {
  return typeof window !== "undefined" ? window.location.origin : undefined;
}
